import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import type { GraphQuery, NodeLabel } from '@/types';

export interface WebAppOption {
  key: string;
  label: string;
}

/**
 * Thin wrapper over `useMy({ resource: 'webapplication' })` that maps the
 * tenant's WebApplications to `{ key, label }` options for the dynamic-traffic
 * picker. A single high-`limit` page is treated as "all" for the picker.
 */
export function useTenantWebApplications({ osint }: { osint: boolean }) {
  const { data, status } = useMy(
    {
      resource: 'webapplication',
      osint,
      query: {
        node: {
          labels: ['WebApplication'] as NodeLabel[],
          relationships: [
            {
              label: 'HAS_WEBPAGE',
              target: {
                labels: ['Webpage'],
                filters: [
                  {
                    field: 'details_filepath',
                    operator: 'STARTS WITH',
                    value: 'webpage/',
                  },
                ],
              },
              // no `optional` -> inner join: apps with no matching child Webpage are excluded
            },
          ],
        },
        limit: 1000,
        page: 0,
      } as unknown as GraphQuery,
    },
    { staleTime: 30_000 }
  );

  const webApps = useMemo<WebAppOption[]>(
    () =>
      (data ?? []).map(w => ({
        key: w.key,
        label: w.name || w.dns || w.key,
      })),
    [data]
  );

  return { webApps, status };
}
